import { debugLog, debugInfo } from "../utils/debug";
import { API_BASE_URL } from "../config/api.js";
import { setupTokenRefresh, stopTokenRefresh } from "../utils/tokenRefresh.js";

const TOKEN_KEY = "authToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const USER_KEY = "userData";
const TOKEN_EXPIRY_KEY = "tokenExpiry";

/**
 * Проверяет валидность токена и возвращает его
 */
export const getValidToken = async () => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !expiry) {
      debugLog("authUtils", "No token or expiry found");
      return null;
    }

    const now = new Date().getTime();
    if (now > parseInt(expiry)) {
      debugLog("authUtils", "Token expired");
      clearAllStorage();
      return null;
    }

    debugInfo("authUtils", "Valid token found");
    return token;
  } catch (error) {
    debugLog("authUtils", "Error checking token validity:", error);
    return null;
  }
};

/**
 * Сохраняет токен авторизации
 */
export const saveAuthToken = (token, expiresIn = 3600) => {
  const expiryTime = new Date().getTime() + expiresIn * 1000;

  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

  debugInfo("authUtils", "Auth token saved");
};

/**
 * Сохраняет refresh token
 */
export const saveRefreshToken = (refreshToken) => {
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  debugInfo("authUtils", "Refresh token saved");
};

/**
 * Получает refresh token
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Сохраняет данные пользователя
 */
export const saveUserData = (userData) => {
  localStorage.setItem(USER_KEY, JSON.stringify(userData));
  debugInfo("authUtils", "User data saved");
};

/**
 * Получает данные пользователя
 */
export const getUserData = () => {
  try {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    debugLog("authUtils", "Error parsing user data:", error);
    return null;
  }
};

/**
 * Очищает все данные авторизации
 */
export const clearAllStorage = () => {
  // Останавливаем автоматическое обновление токена
  stopTokenRefresh();

  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);

  // Очищаем дополнительные данные кэша
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith("cache_") || key.startsWith("userSettings_"))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));

  debugInfo("authUtils", "All storage cleared");
};

/**
 * Проверяет, запущено ли приложение на localhost
 */
export const isLocalhost = () => {
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === ""
  );
};

/**
 * Попытка автоматической IP авторизации (для разработки)
 */
export const attemptIPAutoAuth = async () => {
  if (!isLocalhost()) {
    return null;
  }

  try {
    debugInfo("authUtils", "Attempting IP auto auth for localhost");

    // Имитируем успешную авторизацию для localhost
    const mockUser = {
      id: "dev-user",
      email: "dev@localhost",
      name: "Development User",
      role: "admin",
    };

    const mockToken = "dev-token-" + Date.now();

    saveAuthToken(mockToken, 86400); // 24 часа
    saveUserData(mockUser);

    return mockUser;
  } catch (error) {
    debugLog("authUtils", "IP auto auth failed:", error);
    return null;
  }
};

/**
 * Проверяет, активна ли IP сессия
 */
export const isIPAuthSession = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token && token.startsWith("dev-token-");
};

/**
 * Очищает IP авторизацию
 */
export const clearIPAuth = () => {
  if (isIPAuthSession()) {
    clearAllStorage();
  }
};

/**
 * Аутентификация пользователя через API
 */
export const authenticateUser = async (email, password) => {
  try {
    debugInfo("authUtils", "Attempting user authentication");

    // В разработке можем использовать mock API
    if (isLocalhost() && email === "admin@localhost" && password === "admin") {
      const user = await attemptIPAutoAuth();
      return { success: true, user };
    }

    // Здесь будет реальный API вызов
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (response.ok && (data.access_token || data.token)) {
      // API возвращает access_token, но сохраняем для совместимости
      const token = data.access_token || data.token;
      const refreshToken = data.refresh_token;
      const expiresIn = data.expires_in || data.expiresIn || 3600;
      
      saveAuthToken(token, expiresIn);
      
      // Сохраняем refresh token если он есть
      if (refreshToken) {
        saveRefreshToken(refreshToken);
        debugInfo("authUtils", "Refresh token saved");
      } else {
        debugLog("authUtils", "No refresh token in response");
      }
      
      saveUserData(data.user);

      // Запускаем автоматическое обновление токена
      setupTokenRefresh(expiresIn);

      return { success: true, user: data.user };
    } else {
      return { success: false, error: data.error || data.message || "Authentication failed" };
    }
  } catch (error) {
    debugLog("authUtils", "Authentication error:", error);
    return { success: false, error: "Network error" };
  }
};

/**
 * Регистрация нового пользователя
 */
export const registerUser = async (userData) => {
  try {
    debugInfo("authUtils", "Attempting user registration");

    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, message: data.message };
    } else {
      return { success: false, error: data.message || "Registration failed" };
    }
  } catch (error) {
    debugLog("authUtils", "Registration error:", error);
    return { success: false, error: "Network error" };
  }
};

/**
 * Восстановление пароля
 */
export const forgotPassword = async (email) => {
  try {
    debugInfo("authUtils", "Attempting password recovery");

    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, message: data.message };
    } else {
      return { success: false, error: data.message };
    }
  } catch (error) {
    debugLog("authUtils", "Password recovery error:", error);
    return { success: false, error: "Network error" };
  }
};
